import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import {
  ATTENDANCE_NOTIFICATION_ROLES,
  getDefaultAttendanceGeneralSettings,
  loadAttendanceGeneralSettings,
  saveAttendanceGeneralSettings,
  SCHEDULE_OPTIONS,
} from "../../../../../lib/attendanceGeneralSettings";
import { runAttendanceScheduleRecalc } from "../../../../../lib/attendanceScheduleRunner";

const GeneralSettingsTab = ({ darkMode, parentServiceCommencesOnDate }) => {
  const [settings, setSettings] = useState(getDefaultAttendanceGeneralSettings);
  const [savedSnapshot, setSavedSnapshot] = useState(
    JSON.stringify(getDefaultAttendanceGeneralSettings())
  );

  useEffect(() => {
    const loaded = loadAttendanceGeneralSettings();
    setSettings(loaded);
    setSavedSnapshot(JSON.stringify(loaded));
  }, []);

  const serviceCommencementLabel =
    parentServiceCommencesOnDate &&
    parentServiceCommencesOnDate instanceof Date &&
    !isNaN(parentServiceCommencesOnDate.getTime())
      ? parentServiceCommencesOnDate.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        })
      : "—";

  const isDirty = JSON.stringify(settings) !== savedSnapshot;

  const toggleNotifyRole = (roleValue) => {
    setSettings((prev) => {
      const has = prev.notifyRoles.includes(roleValue);
      const notifyRoles = has
        ? prev.notifyRoles.filter((r) => r !== roleValue)
        : [...prev.notifyRoles, roleValue];
      return { ...prev, notifyRoles };
    });
  };

  const handleSave = () => {
    const saved = saveAttendanceGeneralSettings(settings);
    setSettings(saved);
    setSavedSnapshot(JSON.stringify(saved));
    if (saved.runAttendanceSchedule !== "realtime") {
      runAttendanceScheduleRecalc({ reason: "manual", notify: true });
    }
    toast.success("Attendance general settings saved");
  };

  const labelClass = `shrink-0 text-sm font-medium ${
    darkMode ? "text-gray-300" : "text-gray-700"
  }`;
  const hintClass = `text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`;
  const rowClass =
    "flex flex-nowrap items-center gap-3 overflow-x-auto pb-1 sm:gap-4";
  const sectionTitle = `shrink-0 text-sm font-semibold sm:text-base ${
    darkMode ? "text-white" : "text-gray-800"
  }`;

  return (
    <div className="space-y-6">
      <div>
        <h3 className={`text-lg font-semibold mb-4 ${sectionTitle}`}>
          General Settings
        </h3>

        {/* Service commencement — single row */}
        <div
          className={`rounded-lg border p-4 mb-4 ${
            darkMode
              ? "border-gray-600 bg-gray-800/50"
              : "border-gray-200 bg-gray-50/80"
          }`}
        >
          <div className={rowClass}>
            <span className={labelClass}>Service Commencement On</span>
            <div
              className={`shrink-0 px-4 py-2 rounded border ${
                darkMode
                  ? "bg-gray-700 border-gray-600 text-gray-200"
                  : "bg-gray-100 border-gray-300 text-gray-800"
              }`}
              title="Read-only: set at company level"
            >
              {serviceCommencementLabel}
            </div>
            <span className={`shrink-0 ${hintClass}`}>
              View-only · configured at superadmin level
            </span>
          </div>
        </div>

        {/* Run attendance schedule */}
        <div
          className={`rounded-lg border p-4 mb-4 ${
            darkMode
              ? "border-gray-600 bg-gray-800/50"
              : "border-gray-200 bg-white"
          }`}
        >
          <div className={`${rowClass} mb-3`}>
            <span className={sectionTitle}>Run attendance schedule</span>
          </div>
          <p className={`mb-3 ${hintClass}`}>
            Controls when PR/LOP is recalculated from punches and policies. Realtime
            updates on check-in/out; other options run a batch refresh on the chosen
            interval and refresh calendar checks and status totals.
          </p>
          <div className="flex flex-wrap items-center gap-4 sm:gap-6">
            {SCHEDULE_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`flex cursor-pointer items-center text-sm ${
                  darkMode ? "text-gray-200" : "text-gray-800"
                }`}
              >
                <input
                  type="radio"
                  name="runAttendanceSchedule"
                  className="mr-2"
                  checked={settings.runAttendanceSchedule === opt.value}
                  onChange={() =>
                    setSettings((prev) => ({
                      ...prev,
                      runAttendanceSchedule: opt.value,
                    }))
                  }
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        {/* Notify roles */}
        <div
          className={`rounded-lg border p-4 ${
            darkMode
              ? "border-gray-600 bg-gray-800/50"
              : "border-gray-200 bg-white"
          }`}
        >
          <div className={`${rowClass} mb-2`}>
            <span className={sectionTitle}>Notify roles on recalculation</span>
          </div>
          <p className={`mb-3 ${hintClass}`}>
            Selected roles receive a navbar notification when a scheduled or manual
            attendance recalculation completes.
          </p>
          <div className="flex flex-wrap gap-4">
            {ATTENDANCE_NOTIFICATION_ROLES.map((role) => (
              <label
                key={role.value}
                className={`flex cursor-pointer items-center text-sm ${
                  darkMode ? "text-gray-200" : "text-gray-800"
                }`}
              >
                <input
                  type="checkbox"
                  className="mr-2 rounded"
                  checked={settings.notifyRoles.includes(role.value)}
                  onChange={() => toggleNotifyRole(role.value)}
                />
                {role.label}
              </label>
            ))}
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              isDirty
                ? "rh-btn-primary text-white"
                : darkMode
                ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                : "bg-gray-200 text-gray-500 cursor-not-allowed"
            }`}
          >
            Save settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default GeneralSettingsTab;
